import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/ClerkAuthContext";
import { apiService } from "../../services/api";

interface PoolStats {
  bunpack?: {
    ready: number;
    target: number;
  };
  baseapp?: {
    ready: number;
    target: number;
  };
  skeleton?: {
    ready: number;
    target: number;
  };
  global?: {
    provisioning: number;
    failed: number;
  };
}

export const DevboxPool: React.FC = () => {
  const { getToken } = useAuth();
  const [stats, setStats] = useState<PoolStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [cycling, setCycling] = useState(false);

  useEffect(() => {
    loadStats();
    // Refresh stats every 10 seconds
    const interval = setInterval(loadStats, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      const token = await getToken();
      const response = await apiService.fetch("/api/admin/pool/stats", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to load pool stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const cyclePool = async (packType: string) => {
    if (!window.confirm(`Are you sure you want to cycle the ${packType} pool? This will delete all ready devboxes and recreate them.`)) {
      return;
    }

    setCycling(true);
    try {
      const token = await getToken();
      const response = await apiService.fetch("/api/admin/pool/cycle", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ pack_type: packType }),
      });

      if (response.ok) {
        alert(`Pool cycle initiated for ${packType}`);
        // Refresh stats immediately
        await loadStats();
      } else {
        const error = await response.json();
        alert(`Failed to cycle pool: ${error.error}`);
      }
    } catch (error) {
      console.error("Failed to cycle pool:", error);
      alert("Failed to cycle pool");
    } finally {
      setCycling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Devbox Pool Management</h2>
        <p className="text-sm text-gray-600 mt-1">
          Manage pre-provisioned devboxes for faster application creation.
        </p>
      </div>

      {/* Global Stats */}
      {stats?.global && (
        <div className="mb-6 grid grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="text-sm font-medium text-blue-900">Provisioning</div>
            <div className="text-2xl font-bold text-blue-600">{stats.global.provisioning}</div>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <div className="text-sm font-medium text-red-900">Failed</div>
            <div className="text-2xl font-bold text-red-600">{stats.global.failed}</div>
          </div>
        </div>
      )}

      {/* Pack Type Stats */}
      <div className="space-y-4">
        {Object.entries(stats || {}).map(([packType, packStats]) => {
          if (packType === "global" || typeof packStats !== "object") return null;
          const { ready, target } = packStats as { ready: number; target: number };

          return (
            <div key={packType} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-md font-semibold capitalize">{packType}</h3>
                  <div className="mt-2 flex items-center space-x-4">
                    <div>
                      <span className="text-sm text-gray-600">Ready:</span>
                      <span className="ml-2 text-lg font-bold text-green-600">{ready}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Target:</span>
                      <span className="ml-2 text-lg font-bold text-gray-700">{target}</span>
                    </div>
                  </div>
                  {ready < target && (
                    <div className="mt-1 text-xs text-yellow-600">
                      Pool is below target (deficit: {target - ready})
                    </div>
                  )}
                </div>
                {target > 0 && (
                  <button
                    onClick={() => cyclePool(packType)}
                    disabled={cycling}
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {cycling ? "Cycling..." : "Cycle Pool"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 text-xs text-gray-500">
        Stats refresh automatically every 10 seconds
      </div>
    </div>
  );
};
